/* AUTO-SYNCED from PsyKit.io/lib/rtc/perfect-negotiation.ts — DO NOT EDIT. Regenerate with `npm run sync`. */
/**
 * lib/rtc/perfect-negotiation.ts — the one piece of the WebRTC negotiation
 * state machine worth isolating and testing: the glare (offer-collision)
 * decision from the MDN "perfect negotiation" pattern.
 *
 * Both peers can fire `negotiationneeded` at once (both add tracks). When two
 * offers cross on the wire ("glare"), exactly ONE side must back down or both
 * get stuck. The rule: the **impolite** peer ignores an incoming offer that
 * collides with its own in-flight offer; the **polite** peer always yields
 * (rolls back and accepts). Therapist = impolite, anonymous client = polite.
 *
 * Pure string logic, no DOM — unit-tested under `node --test`.
 */

export interface GlareInputs {
  /** Polite peer (client) yields on collision; impolite peer (therapist) wins. */
  polite: boolean;
  /** Are we currently producing our own offer (setLocalDescription in flight)? */
  makingOffer: boolean;
  /** The peer connection's current signaling state. */
  signalingState: RTCSignalingState;
  /** True between starting and finishing setRemoteDescription(answer). */
  isSettingRemoteAnswerPending: boolean;
  /** Type of the inbound description we're deciding about. */
  descriptionType: RTCSdpType;
}

/**
 * Returns true when THIS peer should drop the inbound offer to resolve glare.
 * Only ever true for the impolite peer, and only for a colliding *offer*.
 */
export function shouldIgnoreOffer(p: GlareInputs): boolean {
  // We can cleanly accept an offer only when we're not mid-offer ourselves and
  // either fully stable or just finishing applying a remote answer.
  const readyForOffer =
    !p.makingOffer && (p.signalingState === "stable" || p.isSettingRemoteAnswerPending);
  const offerCollision = p.descriptionType === "offer" && !readyForOffer;
  return !p.polite && offerCollision;
}
